import type { StudentClaim } from "@/api/studentClaims";
import type { UseQueryResult } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

/**
 * Runs `onDetected` once per classroom visit when the student-claims query succeeds with at least one
 * conflicting claim (same filtered list as the claims banner). Resets when `classroomStableId` changes.
 */
export function useOnFirstStudentClaimsDetected(
	classroomStableId: string | undefined,
	query: UseQueryResult<StudentClaim[]>,
	onDetected: (claims: StudentClaim[]) => void
): void {
	const prevClassroomRef = useRef<string | undefined>(undefined);
	const firedForCurrentClassRef = useRef(false);

	useEffect(() => {
		if (classroomStableId !== prevClassroomRef.current) {
			prevClassroomRef.current = classroomStableId;
			firedForCurrentClassRef.current = false;
		}
	}, [classroomStableId]);

	useEffect(() => {
		if (!classroomStableId || firedForCurrentClassRef.current || !query.isSuccess) {
			return;
		}
		const claims = query.data ?? [];
		if (claims.length === 0) {
			return;
		}
		firedForCurrentClassRef.current = true;
		onDetected(claims);
	}, [classroomStableId, query.isSuccess, query.data, onDetected]);
}
